import {Fragment} from 'react'
import {documentToReactComponents} from '@contentful/rich-text-react-renderer'
import {BLOCKS, INLINES, MARKS, type Document} from '@contentful/rich-text-types'
import {InlineLink} from '@primer/react-brand/esm'

import {documentToPlainTextString, getAnalyticsEvent} from '../../../lib/utils/analytics'
import {ContentfulInlineFootnote} from '../ContentfulFootnotes/ContentfulInlineFootnote'

type ComparisonRichTextOptions = {
  inline: boolean
  analyticsContext: string
  analyticsLocation: string
}

export function renderComparisonRichText(
  document: Document | undefined,
  {inline, analyticsContext, analyticsLocation}: ComparisonRichTextOptions,
) {
  if (!document) return null
  const plainText = documentToPlainTextString(document)
  const rendered = documentToReactComponents(document, {
    renderText: text =>
      text.split('\n').map((line, index) => (
        <Fragment key={index}>
          {index > 0 && <br />}
          {line}
        </Fragment>
      )),
    renderMark: {
      [MARKS.BOLD]: children => <strong>{children}</strong>,
      [MARKS.ITALIC]: children => <em>{children}</em>,
      [MARKS.SUPERSCRIPT]: children => <sup>{children}</sup>,
      [MARKS.STRIKETHROUGH]: children => <s>{children}</s>,
    },
    renderNode: {
      [BLOCKS.PARAGRAPH]: (_, children) => (inline ? children : <p>{children}</p>),
      [BLOCKS.OL_LIST]: (_, children) => <ol>{children}</ol>,
      [BLOCKS.UL_LIST]: (_, children) => <ul>{children}</ul>,
      [BLOCKS.LIST_ITEM]: (_, children) => <li>{children}</li>,
      [INLINES.HYPERLINK]: (node, children) => (
        <InlineLink
          href={node.data.uri}
          {...getAnalyticsEvent({
            action: documentToPlainTextString(node, ' '),
            tag: 'hyperlink',
            context: analyticsContext,
            location: analyticsLocation,
          })}
        >
          {children}
        </InlineLink>
      ),
      [INLINES.EMBEDDED_ENTRY]: node => {
        if (node.data.target.sys.contentType.sys.id === 'inlineFootnote') {
          return (
            <ContentfulInlineFootnote
              component={node.data.target}
              providedInstanceId={plainText}
              analyticsLocation={plainText}
            />
          )
        }

        return null
      },
    },
  })

  // Brand's Cell lays out children in an inline-flex row, so blocks need one wrapper to stack.
  return inline ? rendered : <div>{rendered}</div>
}

export function hasComparisonDescription(document: Document | undefined): boolean {
  if (!document) return false
  return document.content.some(paragraph =>
    paragraph.content.some(node => node.nodeType !== 'text' || Boolean(node.value.trim())),
  )
}
